<template>
  <div>
    <UnitLoading v-if="isLoadingSuggestions" class="my-5" />
    <UnitEmpty v-if="isEmptySuggestions" class="my-5" />
    <div v-for="suggestion in suggestions.data.data.data" :key="suggestion.id">
      <div class="position-relative my-4">
        <font-awesome-icon
          :icon="['far', 'lightbulb']"
          class="suggestion-icon d-inline-block align-center"
          size="2x"
        />
        <div class="mb-3">
          {{ suggestion.text }}
          <span
            :class="`text-${typeMap[suggestion.type].variant}`"
            v-if="suggestion.type"
            >{{ typeMap[suggestion.type].text }}</span
          >
        </div>
      </div>
      <div class="w-100 d-flex" v-if="suggestion.comment">
        <div class="position-relative">
          <b-row>
            <b-col cols="10" offset="2" md="11" offset-md="1">
              <b-iconstack class="msg-type-icon" font-scale="1.5">
                <b-icon stacked icon="chat-fill" variant="primary"></b-icon>
                <b-icon
                  stacked
                  icon="chat"
                  variant="primary"
                  shift-h="3"
                  shift-v="-1"
                  flip-h
                ></b-icon>
              </b-iconstack>
              <div>{{ suggestion.comment.user.nickname }}</div>
              <small class="text-muted"
                >{{ suggestion.comment.created_at | formatDateTime }}
              </small>
              <div>
                <span
                  :class="`text-${typeMap[suggestion.comment.type].variant}`"
                  >{{ typeMap[suggestion.comment.type].text }}</span
                >
                {{ suggestion.comment.comment }}
              </div>
              <div class="mt-2">
                <b-btn
                  v-if="!suggestion.comment.answered_by_count > 0"
                  size="sm"
                  variant="none"
                  @click="moveToAnswers(suggestion.comment.id)"
                >
                  <b-icon
                    flip-h
                    icon="reply-fill"
                    variant="secondary"
                    to
                  ></b-icon>
                </b-btn>
                <b-btn
                  v-if="suggestion.comment.answered_by_count > 0"
                  @click="moveToAnswers(suggestion.comment.id)"
                  size="sm"
                  variant="none"
                >
                  <b-icon
                    icon="chat-fill"
                    variant="secondary"
                    font-scale="1.2"
                  ></b-icon>
                  <span class="ml-2"
                    ><small>{{
                      suggestion.comment.answered_by_count
                    }}</small></span
                  >
                </b-btn>
              </div>
            </b-col>
          </b-row>
        </div>
      </div>
      <div class="text-center ">
        <b-button
          variant="info"
          pill
          class="text-white"
          @click="onChange(suggestion)"
          :disabled="allBtnDisabled"
          >Afficher
          <b-spinner
            class="text-secondary"
            small
            v-if="buttonsLoading[suggestion.id]"
          />
        </b-button>
      </div>
      <hr />
    </div>
  </div>
</template>
<script>
/**
 * @file       Deliberation suggestions component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import Vue from "vue";
import { commentsMixin } from "@/modules/comments/mixins";
import store from "@/store";
import { mapState } from "vuex";
import UnitEmpty from "@/modules/comments/components/unitEmpty.vue";
import UnitLoading from "@/modules/comments/components/unitLoading.vue";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "Suggestions",
  mixins: [commentsMixin],
  components: { UnitEmpty, UnitLoading },

  /**
   * Before route change.
   * 
   * @param  {string}   to     Destination
   * @param  {string}   from   (Unused)
   * @param  {Object}   next   The action
   */
  beforeRouteEnter(to, from, next) {
    next(() => {
      store.state.comments.suggestions.status = "fetching";
      store.commit("comments/setCommentsFilters", {
        reset: true
      });
      store
        .dispatch("deliberations/getDlibr", {
          params: { teacher: to.params.teacher, url: to.params.url }
        })
        .then(({ data }) => {
          const profileId = store.state.auth.profile.data.data.find(u => u).id;
          const dlibrId = data.data.find(d => d).id;
          store.commit("comments/setSuggestionsFilters", {
            reset: true,
            fields: {
              user: profileId,
              deliberation: dlibrId,
              status: 0
            }
          });
          store.dispatch("comments/getSuggestions");
        });
    }).catch(() => {
      next(vm => {
        vm.$root.$bvToast.toast("Aucune délibération trouvée pour cette URL", {
          title: "Erreur",
          variant: "danger"
        });
        vm.$router.push({ name: "home" });
      });
    });
  },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      buttonsLoading: [],
      allBtnDisabled: false
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      callSuggestion: state => state.comments.suggestion.status,
      suggestions: state => state.comments.suggestions
    }),

    /**
     * Check if empty suggestions.
     *
     * @return     {bool}  true or false.
     */
    isEmptySuggestions() {
      return (
        this.suggestions.status === "success" &&
        !this.suggestions.data.data.data.length
      );
    },

    /**
     * Check if loading suggestions.
     *
     * @return     {bool}  true or false.
     */
    isLoadingSuggestions() {
      return this.suggestions.status === "fetching";
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Change suggestions.
     *
     * @param     {Object}  suggestion   The suggestion.
     */
    onChange(suggestion) {
      Vue.set(this.buttonsLoading, suggestion.id, true);
      this.allBtnDisabled = true;
      this.$store
        .dispatch("comments/putSuggestion", {
          suggestion: suggestion.id,
          status: 1
        })
        .then(() => {
          suggestion.comment
            ? this.moveToAnswers(suggestion.comment.id)
            : this.moveToByUrl();
        })
        .catch(() => {
          Vue.set(this.buttonsLoading, suggestion.id, false);
          this.allBtnDisabled = false;
        });
    }
  },
  mounted() {
    if (!this.isActiveJobs) {
      this.onActiveJobs();
    }
  }
};
</script>
<style scoped lang="scss">
.msg-type-icon {
  position: absolute !important;
  left: -25px;
  top: 5px;
}
.suggestion-icon {
  position: absolute !important;
  left: 0px;
  top: 3px;
  ~ div {
    padding-left: 40px;
  }
}
</style>
